import type { ReactNode } from '@frontend/react';
import { useVisibilityController } from '@frontend/react';
import type { UseGlobalTaskCreationProps } from '@frontend/react-domain';
import { useGlobalTaskCreation } from '@frontend/react-domain';
import { useAuthSlice, useTaskSlice } from '@frontend/redux';
import { LinkButton, showToast } from '@frontend/web-react';
import { WebTypedFormData } from '@frontend/web-utils';
import {
  useAnalytics,
  useCreateRecurrenceMutation,
  useCreateTaskMutation,
  useLazyRetrieveClientStatusQuery,
  useUpdateDraftTaskMutation,
} from '@frontend/web/hooks';
import { handleMutation } from '@frontend/web/utils';
import { disconnectSocket, getSocket, initializeSocket } from '@frontend/web/utils/sockets';
import { useNavigate } from '@tanstack/react-router';
import type { CreateTaskContextType } from './createTaskContext';
import { CreateTaskContext } from './createTaskContext';

export type CreateTaskProviderProps = {
  children: ReactNode;
  onCreateTaskCallback: (args: { id: string; type: 'draft' | 'one-off' | 'recurrence' }) => void;
};

export const CreateTaskProvider = ({ children, onCreateTaskCallback }: CreateTaskProviderProps) => {
  const navigate = useNavigate();
  const [isCreateTaskModalOpen, { close: closeCreateTaskModal, open: openCreateTaskModal }] = useVisibilityController();
  const { token } = useAuthSlice();
  const { track } = useAnalytics();
  const { hideIndividualTaskQuotaStep } = useTaskSlice();

  const closeAndClearCreateTaskState = () => {
    closeCreateTaskModal();
    clearCreateTaskState();
  };

  const onSuccessUpdateDraftTask: UseGlobalTaskCreationProps['onSuccessUpdateDraftTask'] = ({ id: taskId, name }) => {
    closeAndClearCreateTaskState();
    onCreateTaskCallback({ id: taskId, type: 'draft' });
    showToast({
      title: `Draft task "${name}" updated.`,
      variant: 'success',
    });
  };

  const onSuccessCreateRecurringTask: UseGlobalTaskCreationProps['onSuccessCreateRecurringTask'] = async ({
    formattedNextStartDate,
    id: recurrenceId,
    latestTaskDate,
    nameOrDefaultName,
  }) => {
    closeAndClearCreateTaskState();
    onCreateTaskCallback({ id: recurrenceId, type: 'recurrence' });
    const additionalMessage = latestTaskDate ? ` We have already started your task for ${formattedNextStartDate}.` : '';

    showToast({
      message: (
        <LinkButton
          className="mr-lg"
          color="success"
          label="See repeating task"
          onClick={() => navigate({ params: { recurrenceId }, to: '/app/recurrence/$recurrenceId' })}
          type="h5"
          withUnderline
        />
      ),
      title: `Repeating task "${nameOrDefaultName}" created.${additionalMessage}`,
      variant: 'success',
    });
  };

  const onSuccessCreateOneOffTask: UseGlobalTaskCreationProps['onSuccessCreateOneOffTask'] = ({
    id: taskId,
    name,
    status,
  }) => {
    closeAndClearCreateTaskState();
    onCreateTaskCallback({ id: taskId, type: 'one-off' });
    showToast({
      message: (
        <LinkButton
          className="mr-lg"
          color={status === 'draft' ? 'warning' : 'success'}
          label="See task"
          onClick={() =>
            navigate({ params: { taskId }, to: `/app/${status === 'draft' ? 'draft-task' : 'task'}/${taskId}` })
          }
          type="h5"
          withUnderline
        />
      ),
      title: `"${name}" ${status === 'draft' ? "was placed in drafts. Complete one of your active tasks, then we'll start working on this task" : 'created'}`,
      variant: status === 'draft' ? 'warning' : 'success',
    });
  };

  const {
    answers,
    clearCreateTaskState,
    createTaskState,
    decrementStep,
    draftTask,
    incrementStep,
    initialQuestions,
    isOnLastStep,
    isStreaming,
    isSubmitting,
    onUpdateAnswer,
    startTaskCreation,
    updateTaskData,
  } = useGlobalTaskCreation({
    // We also want to explicitly reset state
    closeModal: closeAndClearCreateTaskState,
    dependencies: {
      TypedFormData: WebTypedFormData,
      analyticsTrack: track,
      api: {
        useCreateRecurrenceMutation,
        useCreateTaskMutation,
        useLazyRetrieveClientStatusQuery,
        useUpdateDraftTaskMutation,
      },
      handleMutation,
      socket: {
        disconnectSocket,
        getSocket,
        initializeSocket,
      },
    },
    hideIndividualTaskQuotaStep,
    onSuccessCreateOneOffTask,
    onSuccessCreateRecurringTask,
    onSuccessUpdateDraftTask,
    openModal: openCreateTaskModal,
    platform: 'task_source_web_app',
    token: token!,
  });

  const contextValues: CreateTaskContextType = {
    answers,
    closeCreateTaskModal: closeAndClearCreateTaskState,
    createTaskState,
    decrementStep,
    draftTask,
    incrementStep,
    initialQuestions,
    isCreateTaskModalOpen,
    isOnLastStep,
    isStreaming,
    isSubmitting,
    onUpdateAnswer,
    openCreateTaskModal: startTaskCreation,
    updateTaskData,
  };

  return <CreateTaskContext.Provider value={contextValues}>{children}</CreateTaskContext.Provider>;
};
